/**
 * Market Data API Client
 * Handles market data, OHLCV, tickers, and technical indicators
 */

import { BaseApiClient } from './base-client';
import { ApiResponse } from './config';

// Market Data Types
export interface OHLCVData {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface OHLCVResponse {
  symbol: string;
  timeframe: string;
  data: OHLCVData[];
  metadata: {
    source: string;
    last_updated: string;
    record_count: number;
  };
}

export interface TickerData {
  symbol: string;
  price: number;
  change: number;
  change_percent: number;
  high: number;
  low: number;
  volume: number;
  timestamp: string;
  bid?: number;
  ask?: number;
  exchange?: string;
  data_context?: MarketDataContext;
}

export type MarketDataDelayClass = 'public_rest' | 'delayed' | 'broker_live' | 'paper';

export interface MarketDataContext {
  delay_class: MarketDataDelayClass;
  provider: string;
  requested_provider?: string;
  request_context: 'anonymous' | 'internal' | 'session';
  observed_at: string;
  source_timestamp: string;
  cache_max_age_ms: number;
  fallback_applied?: boolean;
  fallback_reason?: string;
}

interface PublicTickerResponse {
  symbol: string;
  bid: number;
  ask: number;
  last: number;
  volume: number;
  change?: number;
  change_percent?: number;
  exchange: string;
  timestamp: string;
  data_context?: MarketDataContext;
}

interface PublicOrderBookResponse {
  symbol: string;
  bids: number[][];
  asks: number[][];
  exchange: string;
  timestamp: string;
  data_context?: MarketDataContext;
}

export function normalizePublicTicker(raw: PublicTickerResponse): TickerData {
  const change = Number(raw.change);
  const changePercent = Number(raw.change_percent);
  return {
    symbol: raw.symbol,
    price: raw.last,
    change: Number.isFinite(change) ? change : Number.NaN,
    change_percent: Number.isFinite(changePercent) ? changePercent : Number.NaN,
    high: Number.NaN,
    low: Number.NaN,
    volume: raw.volume,
    timestamp: raw.timestamp,
    bid: raw.bid,
    ask: raw.ask,
    exchange: raw.exchange,
    data_context: raw.data_context,
  };
}

export interface DataSource {
  name: string;
  type: string;
  base_url: string;
  is_active: boolean;
  rate_limit: number;
  last_updated: string;
}

export interface DataSourcesResponse {
  sources: DataSource[];
}

export interface TechnicalIndicator {
  name: string;
  value: number;
  signal?: 'buy' | 'sell' | 'neutral';
  timestamp: string;
}

export interface MarketAnalysis {
  symbol: string;
  indicators: TechnicalIndicator[];
  trend: 'bullish' | 'bearish' | 'neutral';
  confidence: number;
  timestamp: string;
}

export interface OHLCVRequest {
  symbol: string;
  timeframe?: '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d' | '1w' | '1M';
  start_time?: string;
  end_time?: string;
  limit?: number;
  sources?: string[];
}

export interface SymbolInfo {
  symbol: string;
  name: string;
  type: 'crypto' | 'stock' | 'forex' | 'commodity';
  exchange: string;
  is_active: boolean;
  min_price_increment: number;
  min_quantity_increment: number;
}

export interface ListedSymbol {
  symbol: string;
  display: string;
}

export interface ListedSymbolSearch {
  exchange: string;
  query: string;
  symbols: ListedSymbol[];
}

const LISTED_SEARCH_EXCHANGES = ['binance', 'kraken', 'alpaca'] as const

/** Merge venue finds so Kraken alts and US names share one search. */
export function mergeListedSymbolSearches(
  results: Array<ListedSymbolSearch | null | undefined>,
  query: string
): ListedSymbolSearch {
  const seen = new Set<string>()
  const symbols: ListedSymbol[] = []
  const venues: string[] = []
  for (const result of results) {
    if (!result) continue
    const venue = String(result.exchange || '').trim()
    if (venue && !venues.includes(venue)) venues.push(venue)
    for (const row of result.symbols ?? []) {
      const symbol = String(row.symbol || '').trim().toUpperCase()
      if (!symbol || seen.has(symbol)) continue
      seen.add(symbol)
      symbols.push({
        symbol,
        display: String(row.display || '').trim() || symbol,
      })
    }
  }
  return {
    exchange: venues.length === 1 ? venues[0] ?? 'tape' : 'tape',
    query: query.trim().toUpperCase(),
    symbols,
  }
}

export class MarketDataApiClient extends BaseApiClient {
  /**
   * Get OHLCV data for a symbol
   */
  async getOHLCV(params: OHLCVRequest): Promise<ApiResponse<OHLCVResponse>> {
    const { symbol, ...queryParams } = params;
    const searchParams = new URLSearchParams();

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v.toString()));
        } else {
          searchParams.append(key, value.toString());
        }
      }
    });

    const queryString = searchParams.toString();
    const endpoint = `/api/v1/market-data/market/ohlcv/${encodeURIComponent(symbol)}${queryString ? `?${queryString}` : ''}`;

    return this.get(endpoint);
  }

  /**
   * Get current ticker data for a symbol
   */
  async getTicker(symbol: string): Promise<ApiResponse<TickerData>> {
    const response = await this.get<PublicTickerResponse>(
      `/api/v1/market-data/market/ticker/${encodeURIComponent(symbol)}`
    );
    return {
      ...response,
      data: normalizePublicTicker(response.data),
    };
  }

  /**
   * Get multiple tickers
   */
  async getMultipleTickers(symbols: string[]): Promise<ApiResponse<TickerData[]>> {
    const symbolsParam = symbols.join(',');
    const response = await this.get<PublicTickerResponse[]>(
      `/api/v1/market-data/market/prices?symbols=${encodeURIComponent(symbolsParam)}`
    );
    const rows = Array.isArray(response.data) ? response.data : [];
    return {
      ...response,
      data: rows.map(normalizePublicTicker),
    };
  }

  /**
   * Search names the live venues currently list (Binance, Kraken, Alpaca).
   */
  async searchListedSymbols(query: string): Promise<ApiResponse<ListedSymbolSearch>> {
    const q = query.trim();
    const results = await Promise.all(
      LISTED_SEARCH_EXCHANGES.map(async (exchange) => {
        try {
          const response = await this.get<ListedSymbolSearch>(
            `/api/v1/market-data/market/symbols?q=${encodeURIComponent(q)}&exchange=${exchange}`
          );
          return response.data;
        } catch {
          return null;
        }
      })
    );
    return {
      data: mergeListedSymbolSearches(results, q),
      status: 200,
      success: true,
    };
  }

  /**
   * Get available data sources
   */
  async getDataSources(): Promise<ApiResponse<DataSourcesResponse>> {
    return this.get('/market-data/sources');
  }

  /**
   * Get technical indicators for a symbol
   */
  async getTechnicalIndicators(
    symbol: string,
    indicators: string[] = ['SMA', 'RSI', 'MACD'],
    timeframe: string = '1d'
  ): Promise<ApiResponse<TechnicalIndicator[]>> {
    const indicatorsParam = indicators.join(',');
    return this.get(`/market-data/${symbol}/indicators?indicators=${indicatorsParam}&timeframe=${timeframe}`);
  }

  /**
   * Get market analysis for a symbol
   */
  async getMarketAnalysis(symbol: string): Promise<ApiResponse<MarketAnalysis>> {
    return this.get(`/market-data/${symbol}/analysis`);
  }

  /**
   * Get historical data with pagination
   */
  async getHistoricalData(
    symbol: string,
    timeframe: string = '1d',
    limit: number = 100,
    offset: number = 0
  ): Promise<ApiResponse<OHLCVResponse>> {
    return this.get(`/market-data/${symbol}/historical?timeframe=${timeframe}&limit=${limit}&offset=${offset}`);
  }

  /**
   * Get available symbols
   */
  async getSymbols(type?: string, exchange?: string): Promise<ApiResponse<SymbolInfo[]>> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (exchange) params.append('exchange', exchange);

    const queryString = params.toString();
    return this.get(`/market-data/symbols${queryString ? `?${queryString}` : ''}`);
  }

  /**
   * Get the current public REST quote.
   */
  async getRealTimeQuote(symbol: string): Promise<ApiResponse<{
    symbol: string;
    bid: number;
    ask: number;
    last: number;
    timestamp: string;
    exchange?: string;
    data_context?: MarketDataContext;
  }>> {
    const response = await this.get<PublicTickerResponse>(
      `/api/v1/market-data/market/ticker/${encodeURIComponent(symbol)}`
    );
    return {
      ...response,
      data: {
        symbol: response.data.symbol,
        bid: response.data.bid,
        ask: response.data.ask,
        last: response.data.last,
        timestamp: response.data.timestamp,
        exchange: response.data.exchange,
        data_context: response.data.data_context,
      },
    };
  }

  /**
   * Get market depth/order book
   */
  async getOrderBook(symbol: string, depth: number = 10): Promise<ApiResponse<{
    symbol: string;
    bids: Array<{ price: number; size: number }>;
    asks: Array<{ price: number; size: number }>;
    timestamp: string;
    exchange?: string;
    data_context?: MarketDataContext;
  }>> {
    const response = await this.get<PublicOrderBookResponse>(
      `/api/v1/market-data/market/orderbook/${encodeURIComponent(symbol)}?depth=${depth}`
    );
    const toLevel = (level: number[]) => ({
      price: Number(level[0]),
      size: Number(level[1]),
    });
    return {
      ...response,
      data: {
        symbol: response.data.symbol,
        bids: response.data.bids.map(toLevel),
        asks: response.data.asks.map(toLevel),
        timestamp: response.data.timestamp,
        exchange: response.data.exchange,
        data_context: response.data.data_context,
      },
    };
  }

  /**
   * Get market summary for multiple symbols
   */
  async getMarketSummary(symbols?: string[]): Promise<ApiResponse<{
    total_symbols: number;
    active_symbols: number;
    top_gainers: TickerData[];
    top_losers: TickerData[];
    most_active: TickerData[];
    last_updated: string;
  }>> {
    const symbolsParam = symbols ? symbols.join(',') : '';
    return this.get(`/market-data/summary${symbolsParam ? `?symbols=${symbolsParam}` : ''}`);
  }
}

